import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FeeBand, FeeDefinition } from '@pmp/api';
import { feesColumns } from '@pmp/components';
import { Guard, useGuard } from '@ohpen/react-authz';
import { useTableView } from '@pmp/hooks';
import { lockActionTitle } from '@pmp/utils';
import { useExportTemplatesStore } from '@pmp/stores';
import { ListProps } from '@pmp/types';

import { bands } from '../tableConfig';

const entityType = ActionEntityType.FEES;
const rowKey = 'feeId';

export const FeesList = memo(
  ({ isFetching, valueLists, data, onCreate, onView, onEdit, onDelete }: ListProps<FeeDefinition>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const { tableViews, saveTableView, deleteTableView } = useTableView(entityType);
    const { allowed: showRowSelection } = useGuard({ permissions: ['exportTemplates:create'], flags: ['enableExports'] });
    const { t } = useTranslation();

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.FEES), [getExportTemplates]);

    const getRowActions: (record: FeeDefinition) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View Fee" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['fees:update']} flags={['enableFeesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit Fee"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['fees:delete']} flags={['enableFeesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete Fee"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, t],
    );

    const tableProps: TableProps<ActionEntityType, FeeDefinition, FeeBand> = useMemo(
      () => ({
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns: feesColumns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        valueLists,
        loading: isFetching,
        getRowActions,
        expanded: {
          columns: bands,
          columnsKey: 'bands',
          rowKey: 'index',
        },
        showRowSelection,
        showViews: true,
        tableViews,
        saveTableView,
        deleteTableView,
      }),
      [data, valueLists, isFetching, getRowActions, showRowSelection, tableViews, saveTableView, deleteTableView],
    );

    return (
      <Table {...tableProps}>
        <>
          <Table.ActionsBar>
            <Flex justify="space-between">
              <Space size="middle">
                <Table.GlobalSearch />
              </Space>
              <Space size="middle">
                <Table.ResetFiltersButton />
                <Table.SelectColumnsButton />
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    fileName={`Fees ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                    sheetName="Fees"
                    disabled={isFetching}
                    loadingTemplates={isLoadingExportTemplates}
                    getTemplates={getTemplates}
                    allowSearch={true}
                    exportView
                    flatten
                  />
                </Guard>
                <Guard permissions={['fees:create']} flags={['enableFeesCrud']}>
                  <Table.CreateButton onClick={onCreate} />
                </Guard>
              </Space>
            </Flex>
          </Table.ActionsBar>
          <Table.BulkBar xItemsSelectedLabel="pmp.fees.labels.xFeesSelected">
            <Space size="middle">
              <Guard permissions={['exportTemplates:create']} flags={['enableExports']}>
                <Table.ExportButton
                  fileName={`Fees ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="Fees"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  allowSearch={true}
                  getTemplates={getTemplates}
                  exportView
                  flatten
                />
              </Guard>
            </Space>
          </Table.BulkBar>
        </>
      </Table>
    );
  },
);
